import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { MatFabButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { LEAFLET_CONTROL_DIRECTIVE } from './leaflet-control.directive';

@Component({
  selector: 'lassi-baselayer-toggle',
  imports: [MatFabButton, MatIcon],
  template: `
    <button
      aria-label="Select map base layers"
      data-testid="baselayer_toggle"
      #parentButton
      mat-fab
      color="primary"
      (click)="handleToggleClick()"
    >
      <mat-icon>{{ open() ? 'chevron_left' : 'layers' }}</mat-icon>
    </button>
    @for (layer of baseLayers(); track layer) {
      <button
        [tabindex]="open() ? 0 : -1"
        class="layer"
        [class.isOpened]="open()"
        [class.isActive]="baseLayer() === layer"
        mat-fab
        (click)="handleBaseLayerChange(layer)"
      >
        {{ layer }}
      </button>
    }
  `,
  styles: `
    @use '@angular/material' as mat;
    @use 'theme' as theme;

    .layer {
      position: absolute;
      inset: 0;
      z-index: -1;
      box-shadow: none;

      &.isActive {
        @include mat.fab-overrides(
          (
            container-color:
              mat.get-theme-color(theme.$light-theme, primary, 90),
          )
        );
      }

      &.isOpened {
        box-shadow: var(--mat-fab-container-elevation-shadow);

        @for $i from 2 through 5 {
          &:nth-child(#{$i}) {
            transform: translateX(calc(-100% * #{$i - 1} - 8px * #{$i - 1}));
          }
        }
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [LEAFLET_CONTROL_DIRECTIVE],
})
export class BaseLayerToggleComponent {
  open = signal(false);

  baseLayer = input<string>();
  baseLayers = input.required<string[]>();
  readonly baseLayerChange = output<string>();

  private readonly parentButton = viewChild<MatFabButton>('parentButton');

  @HostListener('document:click', ['$event'])
  handleOtherClicks(event: PointerEvent) {
    if (
      !this.open() ||
      (event.target instanceof Node &&
        this.parentButton()?._elementRef.nativeElement.contains(event.target))
    ) {
      return;
    }
    this.open.set(false);
  }

  handleToggleClick() {
    this.open.update((open) => !open);
  }

  handleBaseLayerChange(layer: string) {
    this.open.set(false);
    this.baseLayerChange.emit(layer);
  }
}
